import { Dropdown, MenuProps, Tooltip } from "antd";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ITaggableHcp } from "../../../../../../@types/Staff";
import {
  AssignedPill,
  PopupWrapper,
  RemoveAssigneeButton,
  SearchInput,
  SearchRow,
  ToggleButton,
  ToolbarContainer,
  assigneePopupCss,
} from "./NoteAssignmentToolbar.styles";

type NoteAssignmentToolbarProps = {
  taggableHcps: ITaggableHcp[] | undefined;
  assignedHcpId: number | null;
  isUrgent: boolean;
  // When true, the assignee can't be removed (edit modal: server enforces
  // tasks can't be downgraded back to notes). The X is hidden in that case.
  lockAssignToggle?: boolean;
  // Optional tooltip shown over the locked toggle while no assignee has been
  // picked yet (e.g. edit modal explaining why this is one-way).
  lockReason?: string;
  // Label override for the assign toggle. Standalone "Add task" entry point
  // uses a shorter label since the modal is always a task.
  assignLabel?: string;
  // Hides the assign toggle entirely (currently unused, kept for callers).
  hideAssignToggle?: boolean;
  // Whether to render the "Mark as urgent" toggle. Urgent is task-only — the
  // caller passes true once an assignee is picked (or when forcing task mode).
  showUrgentToggle?: boolean;
  onAssignToggle: () => void;
  onUrgentToggle: () => void;
  onAssigneeChange: (hcpId: number | null) => void;
  trailing?: ReactNode;
};

function formatHcpLabel(hcp: ITaggableHcp): string {
  const full = `${hcp.firstName ?? ""} ${hcp.lastName ?? ""}`.trim();
  return full ? `${full} (${hcp.email})` : hcp.email;
}

export function NoteAssignmentToolbar({
  taggableHcps,
  assignedHcpId,
  isUrgent,
  lockAssignToggle = false,
  lockReason,
  assignLabel,
  hideAssignToggle = false,
  showUrgentToggle = true,
  onAssignToggle,
  onUrgentToggle,
  onAssigneeChange,
  trailing,
}: NoteAssignmentToolbarProps) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");

  const assignedHcp = useMemo(
    () =>
      assignedHcpId !== null
        ? (taggableHcps ?? []).find((hcp) => hcp.id === assignedHcpId)
        : undefined,
    [taggableHcps, assignedHcpId]
  );

  const filteredHcps = useMemo(() => {
    const list = taggableHcps ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((hcp) => {
      const haystack = `${hcp.firstName ?? ""} ${hcp.lastName ?? ""} ${
        hcp.email ?? ""
      }`.toLowerCase();
      return haystack.includes(term);
    });
  }, [taggableHcps, search]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    const hasAny = (taggableHcps ?? []).length > 0;
    if (!hasAny) {
      return [
        {
          key: "empty",
          label: t("notes_noHcpsAvailable"),
          disabled: true,
        },
      ];
    }
    if (filteredHcps.length === 0) {
      return [
        {
          key: "no-match",
          label: t("notes_noHcpsMatch"),
          disabled: true,
        },
      ];
    }
    return filteredHcps.map((hcp) => ({
      key: String(hcp.id),
      label: formatHcpLabel(hcp),
      onClick: () => onAssigneeChange(hcp.id),
    }));
  }, [taggableHcps, filteredHcps, onAssigneeChange, t]);

  const handleRemoveAssignee = () => {
    onAssigneeChange(null);
    // Flip task mode back off so the toolbar returns to the plain note state.
    // Callers that lock the toggle never render the X, so this branch can't
    // run for them.
    onAssignToggle();
  };

  if (assignedHcp) {
    const pill = (
      <AssignedPill>
        <span>
          {t("patientPage_notes_assignedTo", { hcp: assignedHcp.email })}
        </span>
        {!lockAssignToggle && (
          <RemoveAssigneeButton
            type="button"
            aria-label={t("notes_removeAssignee")}
            onClick={handleRemoveAssignee}
          >
            ×
          </RemoveAssigneeButton>
        )}
      </AssignedPill>
    );

    return (
      <ToolbarContainer>
        {!hideAssignToggle && pill}
        {showUrgentToggle && (
          <ToggleButton
            type="button"
            data-active={isUrgent}
            onClick={onUrgentToggle}
          >
            {isUrgent ? t("notes_markNotUrgent") : t("notes_markUrgent")}
          </ToggleButton>
        )}
        {trailing}
      </ToolbarContainer>
    );
  }

  // No assignee yet: the toggle button is the dropdown trigger. Clicking it
  // pops the HCP list (antd Dropdown handles open/close on click + outside-
  // click) and selecting an item calls onAssigneeChange.
  const trigger = (
    <ToggleButton type="button" disabled={lockAssignToggle}>
      {assignLabel ?? t("notes_assignNoteAsTask")}
    </ToggleButton>
  );

  const wrappedTrigger =
    lockAssignToggle && lockReason ? (
      <Tooltip title={lockReason} color="#4B4B4B">
        <span>{trigger}</span>
      </Tooltip>
    ) : (
      <Dropdown
        menu={{ items: menuItems, style: assigneePopupCss }}
        trigger={["click"]}
        placement="bottomLeft"
        disabled={lockAssignToggle}
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement ?? document.body
        }
        overlayStyle={{ zIndex: 4000 }}
        onOpenChange={(open) => {
          if (!open) setSearch("");
        }}
        dropdownRender={(menu) => (
          <PopupWrapper>
            <SearchRow
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <SearchInput
                autoFocus
                placeholder={t("notes_searchHcp")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchRow>
            {menu}
          </PopupWrapper>
        )}
      >
        {trigger}
      </Dropdown>
    );

  return (
    <ToolbarContainer>
      {!hideAssignToggle && wrappedTrigger}
      {showUrgentToggle && (
        <ToggleButton
          type="button"
          data-active={isUrgent}
          onClick={onUrgentToggle}
        >
          {isUrgent ? t("notes_markNotUrgent") : t("notes_markUrgent")}
        </ToggleButton>
      )}
      {trailing}
    </ToolbarContainer>
  );
}
